"use client";

import { motion } from "motion/react";
import { useState } from "react";

import { type FaqItem } from "@/lib/faq-data";
import { cn } from "@/lib/utils";

/**
 * Collapsible FAQ list shared by the page-section `<Faq>` and the article
 * `<ArticleFaq>`, so every FAQ on the site collapses and looks the same.
 *
 * Answers stay mounted and are hidden by animating their height to zero rather
 * than being unmounted: these same items also feed FAQPage structured data
 * (`faqPageLd` in lib/seo.ts, and the blog route's own JSON-LD), which expects
 * the answer text to be present in the page's HTML.
 */
export function FaqAccordion({ items }: { items: readonly FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: 0.1, delayChildren: 0.4 },
        },
      }}
    >
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <motion.div
            key={item.q}
            className="border-foreground/10 border-b"
            variants={{
              hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
              visible: {
                opacity: 1,
                y: 0,
                filter: "blur(0px)",
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                },
              },
            }}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full cursor-pointer items-baseline gap-4 py-6 text-left md:gap-0"
            >
              <span className="text-text-tertiary w-12 shrink-0 text-[11px] font-semibold tracking-[1.5px]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className={cn(
                  "font-display flex-1 text-xl leading-tight tracking-tight transition-colors duration-300 md:text-[26px]",
                  isOpen
                    ? "text-foreground"
                    : "text-text-tertiary hover:text-foreground/70",
                )}
              >
                {item.q}
              </span>
              <span
                className={cn(
                  "shrink-0 pl-4 text-[22px] leading-none font-medium transition-colors duration-300",
                  isOpen ? "text-foreground" : "text-text-tertiary",
                )}
                aria-hidden
              >
                {isOpen ? "−" : "+"}
              </span>
            </button>
            <motion.div
              initial={false}
              animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              aria-hidden={!isOpen}
            >
              <p className="text-text-secondary max-w-[620px] pb-6 pl-12 text-sm leading-[21px]">
                {item.a}
              </p>
            </motion.div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
